<script lang="ts" setup>
    import { ref } from 'vue'
    import { useResizeObserver } from '@vueuse/core'
    import PkStreamingMarkdown from './PkStreamingMarkdown.vue'

    defineProps<{
        markdown?: string
        innerClass?: string
        isDark?: boolean
        tailLength?: number
    }>()

    // Within this distance of the bottom the reader counts as being there.
    const NEAR_BOTTOM_PX = 8

    const scrollEl = ref<HTMLDivElement>()
    const contentEl = ref<InstanceType<typeof PkStreamingMarkdown>>()
    let stopAutoScroll = false
    // Where the last follow left the box: a scroll event reporting it is the
    // box's own, even when the content grew in the meantime.
    let followedTop: number | undefined

    // The follow stops when the reader leaves the bottom and resumes when they
    // are back. Measured from the bottom, not from the previous position: a
    // tail slides, so its height shrinks as often as it grows, and a shrink
    // moves the position up without the reader doing anything.
    const handleScroll = () => {
        const el = scrollEl.value
        if (!el || el.scrollTop === followedTop) {
            return
        }
        followedTop = undefined
        stopAutoScroll =
            el.scrollHeight - el.clientHeight - el.scrollTop > NEAR_BOTTOM_PX
    }

    // Driven by sizes, not by the prop: the markdown renders at most once per
    // interval, code, formulas and images grow after it, and the box itself
    // can be resized.
    const scrollToBottom = () => {
        const el = scrollEl.value
        if (!el) {
            return
        }
        // Content that fits leaves nothing to have scrolled away from, and no
        // scroll event would ever say the reader is back.
        if (el.scrollHeight <= el.clientHeight) {
            stopAutoScroll = false
        }
        if (stopAutoScroll) {
            return
        }
        el.scrollTop = el.scrollHeight
        followedTop = el.scrollTop
    }

    useResizeObserver([scrollEl, contentEl], scrollToBottom)
</script>

<template>
    <div
        ref="scrollEl"
        class="overflow-auto light-scrollbar"
        @scroll="handleScroll">
        <PkStreamingMarkdown
            ref="contentEl"
            :markdown
            :is-dark
            :tail-length
            :class="innerClass" />
    </div>
</template>
